import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import i18n from '@app/i18n'

import { useLocationStore } from '@entities/Location'

import { useAuth } from '@features/auth'
import { useErrorInfoModal } from '@features/modal'
import AddressItem from '@features/shipping-methods/delivery/address/item'
import DeliveryAddressForm, { FormValues } from '@features/shipping-methods/delivery/form'
import MapDelivery from '@features/shipping-methods/delivery/MapDelivery'
import { useAddressStore } from '@features/shipping-methods/model/useAddressStore'
import { useZonesStore } from '@features/shipping-methods/model/useZonesStore'
import { AddressItemType , ShopItem } from '@features/shipping-methods/types'

import { useSlots } from '@widgets/slots'

import { SHOP_ID } from '@shared/contants'
import {
  CustomerAddressInput,
  useMyAddressesQuery,
  useSuggestionsLazyQuery,
  useZonesForGeoPointLazyQuery,
} from '@shared/generated'
import { useYandexSuggest } from '@shared/hooks/useYandexSuggest'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import ButtonClose from '@shared/ui/button-close'
import LoaderCircle from '@shared/ui/loader-circle'
import Search from '@shared/ui/search'

import { ShopListForPicker } from './ShopList'

import styles from './AddressPickerModal.module.scss'

type ActiveView = 'addAddress' | null

interface GeoPoint {
  latitude: number
  longitude: number
}

interface AddressPickerModalProps {
  className?: string
  onClose?: () => void
}

export default function AddressPickerModal ({
  className,
  onClose,
}: AddressPickerModalProps) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t } = useTranslation('modalDelivery')
  const { t: to } = useTranslation('order')
  const { t: te } = useTranslation('errors')
  const { t: tc } = useTranslation('common')
  const { saveAddress } = useAddressStore()
  const { currentCity } = useLocationStore()
  const isDesktop = useMediaQuery('(min-width: 1280px)')
  const [ activeView, setActiveView ] = useState<ActiveView>(null)
  const [ geoPoint, setGeoPoint ] = useState<GeoPoint | null>(null)
  const [ pendingAddress, setPendingAddress ] = useState<AddressItemType | null>(null)
  const [ pendingShop, setPendingShop ] = useState<ShopItem | null>(null)

  const { isAuthorized, requireRegistration } = useAuth()
  const [ fetchSuggestionDetails ] = useSuggestionsLazyQuery()
  const {
    addAddress,
    setSelectedAddress,
    setDeliveryType,
    setShopId,
    setZoneId,
    savedAddresses,
    selectedAddress,
  } = useAddressStore()
  const [ fetchZonesForGeopoint, { loading: loadingZonesForGeopoint } ] =
    useZonesForGeoPointLazyQuery()
  const {
    loadingAddreses,
    loadingCheckAddressGeopoint,
    zones,
    currentAddress,
    setCurrentAddress,
    setZones,
    fetchCheckAddressGeopoint,
  } = useZonesStore()
  const {
    query: searchInputAddress,
    setQuery: setSearchInputAddress,
    suggestions,
    clearSuggestions,
  } = useYandexSuggest({
    apiKey: import.meta.env.VITE_YANDEX_MAPS_API_KEY_SEARCH,
    lang: i18n.language === 'kk' ? 'kk_KZ' : 'ru_RU',
  })

  // Get saved addresses
  const { data: addressData, loading: loadingAddresses } = useMyAddressesQuery({
    variables: { showPickupShops: false },
  })

  // Get shops for pickup (from myAddresses with showPickupShops: true)
  const { data: shopsData, loading: loadingShops } = useMyAddressesQuery({
    variables: { showPickupShops: true },
  })

  const CORRECT_ZONE_DELIVERY = useMemo(() => {
    if (zones === null) return null
    return zones && zones.length > 0
  }, [ zones ])

  const { openSlotsModal } = useSlots()

  // Filter addresses by current city
  const filteredAddresses = useMemo(() => {
    if (!currentCity) return []

    const listToRender = !requireRegistration
      ? addressData?.myAddresses || []
      : savedAddresses?.filter((el) => !el?.shop_id) || []

    // Filter by city - we need to check if address has shop with matching city_id
    // For now, we'll show all addresses if city filtering is not available in the data
    // This can be enhanced if address data includes city information
    return listToRender.filter((addr) => {
      if (!addr) return false
      // If address has a shop, check if shop's city_id matches
      if (addr.shop && 'city_id' in addr.shop && addr.shop.city_id) {
        return addr.shop.city_id === currentCity.id
      }
      // For delivery addresses without shop, we'll show them all
      // This assumes addresses are already filtered by city selection
      return true
    })
  }, [ addressData, savedAddresses, currentCity, requireRegistration ])

  // Check if defaultShop supports pickup
  const defaultShopSupportsPickup = useMemo(() => {
    return currentCity?.defaultShop?.isPickupDeliveryAvailable === true
  }, [ currentCity ])

  // Filter shops by current city and include defaultShop if it supports pickup
  const filteredShops = useMemo(() => {
    if (!currentCity) return []

    const shops = requireRegistration
      ? savedAddresses
        .filter((el) => el && el.shop_id)
        .map((addr) => {
          if (!addr) return null
          const shop = addr.shop
          return {
            ...addr,
            name: addr.address || (shop && 'name' in shop ? shop.name : '') || '',
            shop_id: addr.shop_id,
            address: addr.address || (shop && 'address' in shop ? shop.address : '') || '',
            shop: shop,
            open_time: (shop && 'open_time' in shop ? shop.open_time : undefined) || 0,
            close_time: (shop && 'close_time' in shop ? shop.close_time : undefined) || 0,
            isPickupDeliveryAvailable: (shop && 'isPickupDeliveryAvailable' in shop ? shop.isPickupDeliveryAvailable : undefined) || false,
          } as unknown as ShopItem
        })
        .filter((shop): shop is ShopItem => shop !== null)
      : (shopsData?.myAddresses || [])
        .filter((addr) => addr && addr.shop_id)
        .map((addr) => {
          if (!addr) return null
          const shop = addr.shop
          return {
            ...addr,
            name: addr.address || (shop && 'name' in shop ? shop.name : '') || '',
            shop_id: addr.shop_id,
            address: addr.address || (shop && 'address' in shop ? shop.address : '') || '',
            shop: shop,
            open_time: (shop && 'open_time' in shop ? shop.open_time : undefined) || 0,
            close_time: (shop && 'close_time' in shop ? shop.close_time : undefined) || 0,
            isPickupDeliveryAvailable: (shop && 'isPickupDeliveryAvailable' in shop ? shop.isPickupDeliveryAvailable : undefined) || false,
          } as unknown as ShopItem
        })
        .filter((shop): shop is ShopItem => shop !== null)

    const cityShops = shops.filter((shop) => {
      if (shop.shop && 'city_id' in shop.shop && shop.shop.city_id) {
        return shop.shop.city_id === currentCity.id
      }
      return true
    }).filter((shop) => shop.isPickupDeliveryAvailable)

    // Add defaultShop if it supports pickup and is not already in the list
    if (defaultShopSupportsPickup && currentCity.defaultShop) {
      const defaultShopId = currentCity.defaultShop.id
      const isDefaultShopInList = cityShops.some((shop) => shop.shop_id === defaultShopId)

      if (!isDefaultShopInList) {
        const defaultShopItem: ShopItem = {
          id: `default-shop-${defaultShopId}`,
          shop_id: defaultShopId,
          address: currentCity.defaultShop.address || '',
          name: currentCity.defaultShop.address || '',
          shop: {
            __typename: 'Shop',
            id: defaultShopId,
            name: currentCity.defaultShop.address || '',
            address: currentCity.defaultShop.address,
            isPickupDeliveryAvailable: true,
            city_id: currentCity.id,
          } as ShopItem['shop'],
          open_time: 0,
          close_time: 0,
          isPickupDeliveryAvailable: true,
        } as ShopItem

        // Add defaultShop at the beginning of the list
        return [ defaultShopItem, ...cityShops ]
      }
    }

    return cityShops
  }, [ shopsData, savedAddresses, currentCity, requireRegistration, defaultShopSupportsPickup ])

  // Check if we should show pickup section
  const shouldShowPickup = useMemo(() => {
    return defaultShopSupportsPickup || filteredShops.length > 0
  }, [ defaultShopSupportsPickup, filteredShops.length ])

  // Initialize pendingShop or pendingAddress with selectedAddress if it exists
  useEffect(() => {
    if (selectedAddress?.shop_id && !pendingShop && !pendingAddress && filteredShops.length > 0) {
      const selectedShopInList = filteredShops.find(
        (shop) => shop.shop_id === selectedAddress.shop_id
      )
      if (selectedShopInList) {
        setPendingShop(selectedShopInList)
      }
    }
    else if (selectedAddress && !selectedAddress.shop_id && !pendingAddress && !pendingShop && filteredAddresses.length > 0) {
      const selectedAddressInList = filteredAddresses.find(
        (addr) => addr && addr.id === selectedAddress.id
      )
      if (selectedAddressInList) {
        setPendingAddress(selectedAddressInList)
      }
    }
  }, [ selectedAddress, filteredShops, filteredAddresses, pendingShop, pendingAddress ])

  useEffect(() => {
    if (currentAddress && searchInputAddress !== currentAddress) {
      setCurrentAddress(null)
      setZones(null)
      clearSuggestions()
    }
  }, [ searchInputAddress ])

  const checkAddressForGeoPoint = async ({
    latitude,
    longitude,
  }: {
    latitude: number
    longitude: number
  }) => {
    const coords = { latitude, longitude }
    setGeoPoint(coords)

    await fetchCheckAddressGeopoint(coords, () => {
      if (currentAddress) {
        setSearchInputAddress(currentAddress)
      }
      setActiveView('addAddress')
    })
  }

  const handleSaveAddress = async (formValues: FormValues) => {
    try {
      if (!geoPoint || !currentAddress) return

      const address: CustomerAddressInput = {
        address: currentAddress,
        address_appartments: formValues.apartment,
        address_floor: formValues.floor,
        address_porch: formValues.entrance,
        address_intercom: formValues.intercom,
        comment: formValues.comment,
        other_recipient: formValues.otherBuyer,
        recipient_name: formValues.otherBuyerName,
        recipient_phone: formValues.otherBuyerPhone,
        customerGeoPoint: geoPoint,
        label: formValues.title,
      }

      const addressWithId = {
        ...address,
        id: address.id || `guest-${nanoid()}`,
      }

      if (!isAuthorized) {
        addAddress(addressWithId as AddressItemType)
      }
      else {
        await saveAddress({
          addressInfo: address,
          geoPoint,
        })
      }

      await handleSelectAddress(addressWithId as AddressItemType)
      setActiveView(null)

      const zones = await fetchZonesForGeopoint({
        variables: {
          point: {
            latitude: geoPoint.latitude,
            longitude: geoPoint.longitude,
          },
        },
      })

      const foundShopId = zones.data?.zonesForGeoPoint?.[0]?.shop_id
      if (foundShopId) {
        setShopId(foundShopId)
      }
      else {
        setShopId(SHOP_ID)
      }
    }
    catch (error) {
      console.error('Error while saving address:', error)
      openErrorInfoModal({
        description: te('save_address'),
      })
    }
  }

  const handleSelectAddress = useCallback(
    async (address: AddressItemType) => {
      setSelectedAddress(address)

      if (address.customerGeoPoint) {
        try {
          const { data } = await fetchZonesForGeopoint({
            variables: {
              point: {
                latitude: address.customerGeoPoint.latitude,
                longitude: address.customerGeoPoint.longitude,
              },
            },
          })

          const zoneId =
            data?.zonesForGeoPoint?.[0]?.firstAvailableSlot?.zone_id
          if (zoneId) {
            setZoneId(zoneId)
            setDeliveryType('slot')
          }
        }
        catch (error) {
          console.error('Error fetching zones for address:', error)
        }
      }
    },
    [ fetchZonesForGeopoint, setDeliveryType, setSelectedAddress, setZoneId ],
  )

  // Handle address selection - clear pickup when delivery is selected
  const handleAddressSelect = useCallback((address: AddressItemType) => {
    setPendingAddress(address)
    setPendingShop(null) // Clear pickup selection
  }, [])

  // Handle shop selection - clear delivery when pickup is selected
  const handleShopClick = useCallback((shop: ShopItem) => {
    setPendingShop(shop)
    setPendingAddress(null) // Clear delivery selection
  }, [])

  // Unified submit handler for both delivery and pickup
  const handleSubmit = async () => {
    if (pendingAddress) {
      // Handle delivery address
      await handleSelectAddress(pendingAddress)
      setDeliveryType('slot')
      onClose?.()
      openSlotsModal()
    }
    else if (pendingShop) {
      // Handle pickup shop
      const address = {
        shop_id: pendingShop.shop_id,
        address: pendingShop.address || pendingShop.shop?.address || '',
        customerGeoPoint: {
          latitude: pendingShop.shop?.shopGeoPoint?.latitude || pendingShop.shop?.geo_point?.latitude,
          longitude: pendingShop.shop?.shopGeoPoint?.longitude || pendingShop.shop?.geo_point?.longitude,
        },
      }

      setSelectedAddress(address as AddressItemType)
      setShopId(pendingShop.shop_id as string)
      setDeliveryType('pickup')
      onClose?.()
      openSlotsModal()
    }
  }

  // Check if any variant is selected
  const hasSelection = pendingAddress || pendingShop

  const handleSuggestionsClick = async (text: string) => {
    const res = await fetchSuggestionDetails({ variables: { address: text } })
    const suggestion = res.data?.getAddressSuggestions?.suggestions?.[0]
    const latitude = suggestion?.data?.geo_lat
    const longitude = suggestion?.data?.geo_lon
    if (latitude && longitude) {
      checkAddressForGeoPoint({ latitude: +latitude, longitude: +longitude })
    }
  }

  const handleAddAddressClick = () => {
    setSearchInputAddress('')
    setCurrentAddress(null)
    setZones(null)
    setGeoPoint(null)
    clearSuggestions()
    setActiveView('addAddress')
  }

  const handleBackFromAddAddress = () => {
    setActiveView(null)
    setSearchInputAddress('')
    setCurrentAddress(null)
    setZones(null)
    clearSuggestions()
  }

  if (activeView === 'addAddress') {
    return (
      <div className={cn(styles.modal, className)}>
        {(loadingZonesForGeopoint ||
          loadingAddreses ||
          loadingCheckAddressGeopoint) && (
          <div className={styles.modal__loading}>
            <LoaderCircle className={styles['modal__loading-circle']} />
          </div>
        )}

        <div className={styles.modal__header}>
          <button
            onClick={handleBackFromAddAddress}
            className={styles.modal__back}
          >
            <ReactSVG src="/icons/back-arrows.svg" wrapper="span" />
          </button>
          <h3 className={styles.modal__title}>{t('new-address')}</h3>
          <ButtonClose
            className={styles.modal__close}
            onClick={onClose}
            theme="grey"
          />
        </div>

        <div className={styles.modal__mapContainer}>
          <div className={styles.modal__mapContent}>
            <Search
              value={currentAddress || searchInputAddress}
              handleChange={setSearchInputAddress}
              placeholder={t('input.placeholder-delivery')}
            />
            {!isDesktop && (
              <span
                onClick={() => setActiveView('addAddress')}
                className={styles.modal__touchMap}
              >
                <ReactSVG src="/icons/map-pin.svg" wrapper="span" />
                {t('button.touch-map')}
              </span>
            )}
            {CORRECT_ZONE_DELIVERY === false && (
              <p className={styles.modal__message}>
                {to('address.invalid_zone')}
              </p>
            )}
            {(CORRECT_ZONE_DELIVERY &&
              searchInputAddress &&
              !suggestions.length) ||
              (currentAddress && (
                <DeliveryAddressForm
                  onSubmit={handleSaveAddress}
                  isZone={CORRECT_ZONE_DELIVERY === true}
                />
              ))}

            {!currentAddress &&
              suggestions.map((el, i) => (
                <div
                  key={`${el.title}-${i}`}
                  onClick={() => {
                    handleSuggestionsClick(
                      el.title.text + ` ${el.subtitle?.text}`,
                    )
                  }}
                  className={styles.modal__foundAddress}
                >
                  <span className={styles.modal__foundAddressTitle}>
                    {el.title.text}
                  </span>
                  {el.subtitle?.text && (
                    <span className={styles.modal__foundAddressSubtitle}>
                      , {el.subtitle.text}
                    </span>
                  )}
                </div>
              ))}
          </div>

          {isDesktop && (
            <MapDelivery
              newAddress={true}
              onChangeAddressMap={checkAddressForGeoPoint}
              targetGeoPoint={geoPoint}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(styles.modal, className)}>
      {(loadingAddresses || loadingShops) && (
        <div className={styles.modal__loading}>
          <LoaderCircle className={styles['modal__loading-circle']} />
        </div>
      )}

      <div className={styles.modal__header}>
        <h3 className={styles.modal__title}>{t('address')}</h3>
        <ButtonClose
          className={styles.modal__close}
          onClick={onClose}
          theme="grey"
        />
      </div>

      <div className={styles.modal__content}>
        {/* First Row: Delivery Addresses */}
        <div className={cn(styles.modal__row, !shouldShowPickup && styles['modal__row--fullHeight'])}>
          <div className={styles.modal__rowHeader}>
            <h4 className={styles.modal__rowTitle}>{tc('delivery')}</h4>
            <Button
              onClick={handleAddAddressClick}
              type="button"
              theme="secondary"
              size={isDesktop ? 'large' : 'medium'}
            >
              {t('button.address')}
            </Button>
          </div>

          {loadingAddresses ? (
            <div className={styles.modal__loading}>Loading...</div>
          ) : filteredAddresses.length > 0 ? (
            <div className={cn(styles.modal__addressList, !shouldShowPickup && styles['modal__addressList--fullHeight'])}>
              {filteredAddresses.map((item, index) => (
                <AddressItem
                  key={`${item?.id}-${index}`}
                  address={item as AddressItemType}
                  selected={pendingAddress?.id === item?.id}
                  onSelect={() => item && handleAddressSelect(item)}
                />
              ))}
            </div>
          ) : (
            <p className={styles.modal__empty}>
              {t('no_addresses') || 'No saved addresses'}
            </p>
          )}

        </div>

        {/* Second Row: Pickup Shops - Only show if pickup is available */}
        {shouldShowPickup && (
          <div className={styles.modal__row}>
            <div className={styles.modal__rowHeader}>
              <h4 className={styles.modal__rowTitle}>{tc('pickup')}</h4>
            </div>

            {loadingShops ? (
              <div className={styles.modal__loading}>Loading...</div>
            ) : filteredShops.length > 0 ? (
              <ShopListForPicker
                shops={filteredShops.filter((s): s is ShopItem => s !== null)}
                handleShopClick={handleShopClick}
                handleDeleteAddress={() => {}}
                selectedShopId={pendingShop?.shop_id}
              />
            ) : (
              <p className={styles.modal__empty}>
                {t('no_shops') || 'No shops available in this city'}
              </p>
            )}
          </div>
        )}

        {/* Single Choose Button for both variants */}
        {(filteredAddresses.length > 0 || (shouldShowPickup && filteredShops.length > 0)) && (
          <Button
            disabled={!hasSelection}
            type="button"
            size={!isDesktop ? 'large' : 'medium'}
            onClick={handleSubmit}
            className={styles.modal__submitButton}
          >
            {tc('choose')}
          </Button>
        )}
      </div>
    </div>
  )
}
